import type { Hour, OpeningHour } from "@/graphql/sdk/__generated__";
import { AllowedLanguages } from "@/types/generic";

export function isOpenNow(openingHours: OpeningHour[]) {
  let isOpenNow = false;

  openingHours.every((openingHour) => {
    if (isOpenOnDay(openingHour)) {
      isOpenNow = true;
      return false;
    }

    return true;
  });

  return isOpenNow;
}

export function isOpenOnDay(
  openingHour: OpeningHour,
  dayOfWeek: number = new Date().getDay(),
) {
  if (dayOfWeek !== openingHour.day) return false;

  let isOpenOnDay = false;

  openingHour.hours?.every((hour) => {
    if (!hour) return true;

    const isOpenInTimeslot = isOpenOnTime(hour);

    if (isOpenInTimeslot) {
      isOpenOnDay = true;
      return false;
    }

    return true;
  });

  return isOpenOnDay;
}

export function isOpenOnTime(hour: Hour, date: Date = new Date()) {
  if (!hour?.opening_hour || !hour.closing_hour) return false;

  const startTime = hour.opening_hour;
  const endTime = hour.closing_hour;

  const startDate = new Date(date.getTime());
  startDate.setHours(Number(startTime.split(":")[0]));
  startDate.setMinutes(Number(startTime.split(":")[1]));

  const endDate = new Date(date.getTime());
  endDate.setHours(Number(endTime.split(":")[0]));
  endDate.setMinutes(Number(endTime.split(":")[1]));

  return startDate < date && endDate > date;
}

export function nextClosingHour(
  openingHours: OpeningHour[],
  date: Date = new Date(),
) {
  let nextClosingHour: Hour | null = null;

  openingHours.every((openingHour) => {
    if (isOpenOnDay(openingHour, date.getDay())) {
      openingHour.hours?.every((hour) => {
        if (isOpenOnTime(hour, date)) {
          nextClosingHour = hour;
          return false;
        }

        return true;
      });
    }

    return true;
  });

  return nextClosingHour;
}

export function getNextOpeningHour(openingHours: OpeningHour[]) {
  const currentTime = new Date();

  const currentDayOfWeek = currentTime.getDay();
  const currentHour = currentTime.getHours();
  const currentMinute = currentTime.getMinutes();

  for (const hours of openingHours) {
    if (hours.day === currentDayOfWeek && hours.hours) {
      for (const slot of hours.hours) {
        const openingTime = slot.opening_hour.split(":");
        const openingHour = parseInt(openingTime[0], 10);
        const openingMinute = parseInt(openingTime[1], 10);

        if (
          currentHour < openingHour ||
          (currentHour === openingHour && currentMinute < openingMinute)
        ) {
          return {
            day: hours.day,
            opening_hour: slot.opening_hour,
          };
        }
      }
    }
  }

  // If no opening hours found for the current day or all slots have passed, find the next day's opening hour.
  for (const hours of openingHours) {
    if (hours.hours && hours.day > currentDayOfWeek) {
      return {
        day: hours.day,
        opening_hour: hours.hours[0].opening_hour,
      };
    }
  }

  return null; // No next opening hour found
}

export function dayNumberToString(day: number, locale: AllowedLanguages) {
  const days = {
    [AllowedLanguages.En]: [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ],
    [AllowedLanguages.Nl]: [
      "Zondag",
      "Maandag",
      "Dinsdag",
      "Woensdag",
      "Donderdag",
      "Vrijdag",
      "Zaterdag",
    ],
    [AllowedLanguages.Fr]: [
      "Dimanche",
      "Lundi",
      "Mardi",
      "Mercredi",
      "Jeudi",
      "Vendredi",
      "Samedi",
    ],
  };
  return days[locale][day];
}
